// src/hooks/useOrgRuntime.js
//
// Pass 83B.1 §8/§16 — the ONE place the instructor workspace resolves an organization's operating runtime
// (timezone + current season + schedule-visibility source) so My Schedule and My Calendar share EXACTLY the
// same selected-organization context. It loads the org doc for the strongest runtime record, but a fetch
// FAILURE is never flattened to `{}` (which would resolve to the generic UTC default and could silently
// downgrade a known SAS org from Asia/Tokyo → UTC): on failure it falls back to the selected-organization
// membership context, only trusts a timezone that is EXPLICITLY present, and surfaces `orgRuntimeError`
// rather than inventing a zone. The no-tz fallback is the app's final fallback zone, never the browser's.

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { resolveOrgTimezone, resolveOrgSeasonId } from '../utils/organizationRuntime';
import { LEGACY_OPS_TIMEZONE } from '../utils/opsTime';

export function useOrgRuntime(orgId, selectedOrganization = null) {
  const [orgDoc, setOrgDoc] = useState(null);
  const [orgLoadFailed, setOrgLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOrgDoc(null); setOrgLoadFailed(false);
    if (!orgId) { return undefined; }
    getDoc(doc(db, 'organizations', orgId))
      .then((s) => { if (!cancelled) setOrgDoc(s.exists() ? { id: s.id, ...s.data() } : {}); })
      .catch(() => { if (!cancelled) setOrgLoadFailed(true); });
    return () => { cancelled = true; };
  }, [orgId]);

  // Strongest available runtime record: the loaded org doc, else the selected-organization membership.
  const orgRuntime = (orgDoc && !orgLoadFailed) ? orgDoc : (selectedOrganization || null);
  const explicitTz = !!(orgRuntime && (orgRuntime.timezone || orgRuntime.timeZone));
  const orgTz = explicitTz ? resolveOrgTimezone(orgRuntime) : '';        // '' until an explicit zone is known
  const tz = orgTz || LEGACY_OPS_TIMEZONE;                                // final-fallback zone, never the browser
  const orgRuntimeError = !!orgId && orgLoadFailed && !explicitTz;        // failed fetch AND no known zone
  const seasonId = (orgDoc && !orgLoadFailed)
    ? resolveOrgSeasonId(orgDoc)
    : resolveOrgSeasonId(selectedOrganization || {});

  return { orgDoc, orgLoadFailed, orgRuntime, orgTz, tz, orgRuntimeError, seasonId };
}

export default useOrgRuntime;
